import { useState, useEffect, useCallback } from "react";
import { Scale, LogOut, Inbox, Settings, BarChart3, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  contacted: "bg-blue-100 text-blue-800",
  closed: "bg-emerald-100 text-emerald-800",
};

// ── Stats Cards ────────────────────────────────────────────
function StatsCards({ stats }) {
  const cards = [
    { label: "Total", value: stats.total, color: "bg-[#1a1a2e]" },
    { label: "Pending", value: stats.pending, color: "bg-amber-600" },
    { label: "Contacted", value: stats.contacted, color: "bg-blue-600" },
    { label: "Closed", value: stats.closed, color: "bg-emerald-600" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-xl p-5 border border-[#e8e3db]">
          <div className={`w-8 h-8 rounded-lg ${c.color} flex items-center justify-center mb-3`}>
            <BarChart3 className="h-4 w-4 text-white" />
          </div>
          <p className="text-2xl font-heading font-extrabold text-[#1a1a2e]">{c.value}</p>
          <p className="text-xs text-[#6b6560] mt-1">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

// ── Consultations Table ────────────────────────────────────
function ConsultationsTab() {
  const [consultations, setConsultations] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/consultations`, axCfg),
        axios.get(`${API}/admin/stats`, axCfg),
      ]);
      setConsultations(cRes.data);
      setStats(sRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [setConsultations, setStats, setLoading]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/consultations/${id}`, { status }, axCfg);
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteConsultation = async (id) => {
    if (!window.confirm("Delete this consultation?")) return;
    try {
      await axios.delete(`${API}/admin/consultations/${id}`, axCfg);
      toast.success("Deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading) return <div className="text-center py-12 text-[#6b6560]">Loading...</div>;

  return (
    <div data-testid="admin-consultations-tab">
      <StatsCards stats={stats} />

      {consultations.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#e8e3db]" data-testid="empty-inbox">
          <Inbox className="h-12 w-12 text-[#d5cfc6] mx-auto mb-4" />
          <p className="text-[#6b6560] font-medium">No consultations yet</p>
          <p className="text-[#a09890] text-sm mt-1">New submissions will appear here</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="consultations-list">
          {consultations.map((c) => (
            <div key={c.id} data-testid={`consultation-${c.id}`} className="bg-white rounded-xl border border-[#e8e3db] p-5 hover:shadow-sm transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1.5">
                    <h3 className="font-semibold text-[#1a1a2e] truncate">{c.full_name}</h3>
                    <Badge className={`${STATUS_COLORS[c.status] || STATUS_COLORS.pending} border-0 rounded-full text-xs font-medium`}>
                      {c.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-[#6b6560] mb-1">{c.case_type} &middot; {c.email} &middot; {c.phone}</p>
                  <p className="text-sm text-[#1a1a2e]/70 line-clamp-2">{c.description}</p>
                  {c.preferred_date && <p className="text-xs text-[#a09890] mt-2">Preferred: {c.preferred_date} {c.preferred_time}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select value={c.status} onValueChange={(v) => updateStatus(c.id, v)}>
                    <SelectTrigger className="w-[130px] h-9 rounded-lg text-xs border-[#e8e3db]" data-testid={`status-select-${c.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" onClick={() => deleteConsultation(c.id)} data-testid={`delete-${c.id}`} className="h-9 w-9 text-red-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Site Settings Tab ──────────────────────────────────────
function SiteSettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/site-settings`, axCfg)
      .then(({ data }) => setSettings(data))
      .catch(() => toast.error("Failed to load settings"));
  }, [setSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, settings, axCfg);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-center py-12 text-[#6b6560]">Loading...</div>;

  const fields = [
    { key: "firm_name", label: "Firm Name" },
    { key: "phone", label: "Phone Number" },
    { key: "email", label: "Email Address" },
    { key: "address_line1", label: "Address Line 1" },
    { key: "address_line2", label: "Address Line 2" },
    { key: "whatsapp", label: "WhatsApp Number" },
  ];

  return (
    <div data-testid="admin-settings-tab" className="bg-white rounded-2xl border border-[#e8e3db] p-6 md:p-8">
      <h2 className="font-heading text-xl font-bold text-[#1a1a2e] mb-6">Firm Settings</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {fields.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-[#1a1a2e] mb-2">{f.label}</label>
            <Input
              data-testid={`setting-${f.key}`}
              value={settings[f.key] || ""}
              onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
              className="rounded-xl border-[#d5cfc6] bg-[#faf8f4] focus:border-[#1a5c4a] h-11"
            />
          </div>
        ))}
      </div>
      <Button
        data-testid="save-settings-btn"
        onClick={handleSave}
        disabled={saving}
        className="mt-6 bg-[#1a5c4a] text-white hover:bg-[#154a3c] rounded-full px-8 h-11 font-semibold"
      >
        {saving ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────
export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("inbox");

  const tabs = [
    { id: "inbox", label: "Inbox", icon: Inbox },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#f5f0e8]" data-testid="admin-dashboard">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="bg-[#1a1a2e] sticky top-0 z-50" data-testid="admin-header">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <Scale className="h-5 w-5 text-[#1a5c4a]" />
            <span className="font-heading text-base font-bold text-white">Admin Portal</span>
          </div>
          <Button
            data-testid="admin-logout-btn"
            variant="ghost"
            onClick={onLogout}
            className="text-white/60 hover:text-white hover:bg-white/10 rounded-full px-4 h-9 text-sm"
          >
            <LogOut className="h-4 w-4 mr-2" />Logout
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  tab === t.id
                    ? "bg-[#1a1a2e] text-white"
                    : "bg-white text-[#6b6560] hover:bg-[#e8e3db] border border-[#e8e3db]"
                }`}
              >
                <Icon className="h-4 w-4" />{t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {tab === "inbox" && <ConsultationsTab />}
        {tab === "settings" && <SiteSettingsTab />}
      </div>
    </div>
  );
}
